import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Minus, Plus } from "lucide-react";
import { api } from "../../context/AuthContext";
import { PriceEditor } from "./PriceEditor";

const groupByProduct = (rows, catalog) => {
  const groups = {};
  rows.forEach((r) => {
    const p = catalog[r.product_id];
    if (!p) return;
    groups[r.product_id] ??= { id: r.product_id, ...p, lines: [] };
    groups[r.product_id].lines.push(r);
  });
  const order = Object.keys(catalog);
  return Object.values(groups).sort(
    (a, b) => order.indexOf(a.id) - order.indexOf(b.id),
  );
};

const StockLine = ({ line, onChange }) => {
  const [draft, setDraft] = useState(String(line.qty));
  useEffect(() => setDraft(String(line.qty)), [line.qty]);
  const id = `${line.product_id}-${line.size}-${line.colour}`.toLowerCase();
  return (
    <tr data-testid={`stock-line-${id}`} className="border-t border-ink/10">
      <td className="py-3 pr-4 font-mono text-xs text-ink">{line.size}</td>
      <td className="py-3 pr-4 text-sm text-mutedink">{line.colour}</td>
      <td className="py-3 pr-4">
        <span
          data-testid={`stock-qty-${id}`}
          className={`font-mono text-sm ${line.qty === 0 ? "text-oxblood" : "text-ink"}`}
        >
          {line.qty === 0 ? "Sold out" : line.qty}
        </span>
      </td>
      <td className="py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid={`stock-minus-${id}`}
            onClick={() => onChange(line, { delta: -1 })}
            className="border border-ink/20 p-1.5 hover:border-oxblood hover:text-oxblood"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            data-testid={`stock-plus-${id}`}
            onClick={() => onChange(line, { delta: 1 })}
            className="border border-ink/20 p-1.5 hover:border-oxblood hover:text-oxblood"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <input
            type="number"
            min="0"
            data-testid={`stock-set-input-${id}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-20 border border-ink/15 px-2 py-1.5 font-mono text-xs text-ink focus:outline-none focus:border-oxblood"
          />
          <button
            type="button"
            data-testid={`stock-set-btn-${id}`}
            onClick={() =>
              onChange(line, { qty: Math.max(0, parseInt(draft || "0", 10)) })
            }
            className="font-mono text-[10px] uppercase tracking-[0.2em] border border-ink px-3 py-1.5 hover:bg-ink hover:text-parchment transition-colors"
          >
            Set
          </button>
        </div>
      </td>
    </tr>
  );
};

export const InventoryPanel = ({ catalog, onPriceChange }) => {
  const [rows, setRows] = useState([]);

  const load = async () => {
    const { data } = await api.get("/admin/inventory");
    setRows(data);
  };
  useEffect(() => {
    load();
  }, []);

  const change = async (line, patch) => {
    try {
      const { data } = await api.patch("/admin/inventory", {
        product_id: line.product_id,
        size: line.size,
        colour: line.colour,
        ...patch,
      });
      setRows((rs) =>
        rs.map((r) =>
          r.product_id === data.product_id &&
          r.size === data.size &&
          r.colour === data.colour
            ? data
            : r,
        ),
      );
    } catch {
      toast.error("Could not update stock");
    }
  };

  const total = rows.reduce((n, r) => n + r.qty, 0);

  return (
    <section data-testid="admin-inventory" className="space-y-10">
      <p
        data-testid="admin-units-on-hand"
        className="font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink"
      >
        {total} units on hand
      </p>
      {groupByProduct(rows, catalog).map((g) => (
        <div
          key={g.id}
          data-testid={`stock-group-${g.id}`}
          className="bg-white border border-ink/10 p-6"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mutedink">
            {g.brand}
          </p>
          <h3 className="font-serif text-xl text-ink mt-1 mb-4">
            {g.name}
            <PriceEditor
              productId={g.id}
              price={g.price}
              onSaved={(price) => onPriceChange(g.id, price)}
            />
          </h3>
          <table className="w-full text-left">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-mutedink">
                <th className="pb-2 pr-4">Size</th>
                <th className="pb-2 pr-4">Colour</th>
                <th className="pb-2 pr-4">In stock</th>
                <th className="pb-2">Adjust</th>
              </tr>
            </thead>
            <tbody>
              {g.lines.map((l) => (
                <StockLine
                  key={`${l.size}-${l.colour}`}
                  line={l}
                  onChange={change}
                />
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
};
